/**
 * Main Line Items tab: table with NEX match expand, flags, revert, unmatch.
 * Kept under 500 lines.
 */
import React from 'react';
import {
    Box,
    Button,
    Center,
    Collapse,
    Flex,
    HStack,
    Icon,
    IconButton,
    Table,
    Tag,
    Tbody,
    Td,
    Text,
    Thead,
    Tooltip,
    Tr,
} from '@chakra-ui/react';
import { TbArrowBackUp, TbPlus, TbUnlink } from 'react-icons/tb';
import type { ReconciliationMatchNote } from '../../types/invoice';
import type { InvoiceLineItemsSectionProps, LineItemField } from './InvoiceLineItemsSection';
import { NexMatchSourceBadge } from './NexMatchSourceBadge';
import { getLineStatusTagColor, getLineStatusDisplayText } from '../../utils/lineStatusTag';
import {
    getSignedNexLineDisplayQty,
    getSignedNexLineDisplayTotal,
    getQtyColumnLabel,
    getQtyColumnTooltip,
} from '../../utils/nexLineQuantity';
import { formatNexMatchedLinePoLabel } from '../../utils/nexMatchedLinePoLabel';
import { isNexLineSelectionIssue } from '../../utils/invoiceLineItemTabOrangeFlags';
import { fmtDollar } from '../../hooks/invoice/detailForm/invoiceDetailFormFormatters';
import { parseSignedQtyCommit } from '../../utils/invoiceNumericInput';
import {
    INVOICE_TAB_ACCENT,
    INVOICE_TAB_ACCENT_HOVER,
    InvoiceIssueFlagButton,
    InvoicePartGridTableContainer,
    InvoiceMatchNoteInfoButton,
    InvoiceMatchedNexToggleButton,
    InvoiceSuggestionBulbButton,
    InvoiceMatchedNexExpandAllHeaderControl,
    InvoiceStandardHeaderRow,
    buildListPriceHeaderContent,
    getInvoiceItemsTableSx,
    InvoicePartTableRowActionsCell,
    VENDOR_MISMATCH_SUGGESTION_DISABLED_REASON,
    invoiceMatchedNexActionsColumnFlexProps,
    invoiceMatchedNexCollapseHeaderTdSx,
    invoiceMatchedNexDetailDataRowTrSx,
    invoicePartGridLandingInputPropsWithCaretAtEnd,
    invoicePartTableActionsIconStackProps,
} from './InvoiceTabShared';
import { LineItemGridLandingEditable } from './LineItemGridLandingEditable';
import { useInvoicePartGridInteractions } from './useInvoicePartGridInteractions';
import { useInvoicePartTableRowDrag } from './useInvoicePartTableRowDrag';

export function InvoiceLineItemsTab(props: InvoiceLineItemsSectionProps) {
    const {
        invoice,
        editedFields,
        lineItemBackendIndexByCurrentIndex,
        unmatchedPartIndices,
        localMatchedNexOverrides,
        flaggedPartIndices,
        expandedMatchedNexRows,
        setExpandedMatchedNexRows,
        setUnmatchConfirmModal,
        addLineItem,
        addLineItemAbove,
        addLineItemAt,
        duplicateLineItemAt,
        removeLineItem,
        reorderLineItems,
        updateLineItem,
        restoreLineItemRowsSnapshot,
        setEditingNumericField,
        setEditingNumericValues,
        openRevertModal,
        openReconciliationDetailModal,
        getLineItemStringValue,
        isLineItemFieldEdited,
        getIssuesForPart,
        getLineItemExpandKey,
        getIssueTooltip,
        getPartNumberChoiceIssue,
        getMissingInNexSuggestion,
        renderLineItemNumericCell,
        isReadOnly,
        suppressLineItemValueChangedRevertUi = false,
        onLineItemImageHighlightChange,
        showTableGridlines = true,
        nexLineQuantityBasis = 'qtyRec',
    } = props as InvoiceLineItemsSectionProps & { isReadOnly?: boolean };

    const showLineItemValueChangedRevert = (rowIndex: number, field: LineItemField) =>
        !suppressLineItemValueChangedRevertUi && isLineItemFieldEdited(rowIndex, field);

    const lineItems = editedFields.lineItems ?? invoice?.lineItems ?? [];
    const hasItems = lineItems.length > 0;
    const qtyColumnLabel = getQtyColumnLabel(invoice?.invoiceType, nexLineQuantityBasis);
    const qtyColumnTooltip = getQtyColumnTooltip(invoice?.invoiceType, nexLineQuantityBasis);

    const spreadsheet = useInvoicePartGridInteractions({
        rowCount: lineItems.length,
        rows: lineItems,
        getStringValue: getLineItemStringValue,
        updateCell: updateLineItem,
        restoreRowsSnapshot: restoreLineItemRowsSnapshot,
        isReadOnly: !!isReadOnly,
        setEditingNumericField: setEditingNumericField ?? (() => {}),
        setEditingNumericValues,
        numericKeyPrefix: 'lineItem',
    });

    const rowDrag = useInvoicePartTableRowDrag({ isReadOnly: !!isReadOnly, onReorder: reorderLineItems });

    if (!hasItems) {
        return (
            <>
                <Box textAlign="center" py={6}>
                    <Button
                        size="sm"
                        bg={INVOICE_TAB_ACCENT}
                        color="white"
                        leftIcon={<Icon as={TbPlus as React.ElementType} boxSize={4} />}
                        onClick={addLineItem}
                        _hover={{ bg: INVOICE_TAB_ACCENT_HOVER }}
                        isDisabled={!!isReadOnly}
                    >
                        Add Line Item
                    </Button>
                </Box>
            </>
        );
    }

    const roNotFound = !!invoice?.reconciliationIssues?.roNumberNotFound;
    const suppressFlagAndSuggestionUi = !!invoice?.reconciliationIssues?.vendorNameMismatch;
    const vendorMismatchDisablesSuggestions = !!invoice?.reconciliationIssues?.vendorNameMismatch;
    const matchedNexLines = props.effectiveMatchedNexLines ?? invoice?.reconciliationIssues?.matchedNexLines ?? [];
    const lineItemsFromBackend = invoice?.lineItems ?? [];
    const useDirectIndex =
        lineItems.length === lineItemsFromBackend.length &&
        lineItemBackendIndexByCurrentIndex.length === lineItems.length &&
        lineItemBackendIndexByCurrentIndex.every((bi, i) => bi === i);
    const matchedNexByCurrentIndex: Record<number, any> = {};
    if (useDirectIndex) {
        matchedNexLines.forEach((m: { partIndex: number; nexLine: any }) => { matchedNexByCurrentIndex[m.partIndex] = m.nexLine; });
    } else {
        const matchedNexByBackendIndex: Record<number, any> = Object.fromEntries(matchedNexLines.map((m: { partIndex: number; nexLine: any }) => [m.partIndex, m.nexLine]));
        lineItemBackendIndexByCurrentIndex.forEach((backendIdx, currentIdx) => {
            if (backendIdx != null && matchedNexByBackendIndex[backendIdx] !== undefined) matchedNexByCurrentIndex[currentIdx] = matchedNexByBackendIndex[backendIdx];
        });
    }
    unmatchedPartIndices.forEach((idx) => { delete matchedNexByCurrentIndex[idx]; });
    Object.entries(localMatchedNexOverrides).forEach(([idxStr, nexLine]) => {
        const idx = parseInt(idxStr, 10);
        if (!Number.isNaN(idx)) matchedNexByCurrentIndex[idx] = nexLine;
    });
    const baseMatchNotes: ReconciliationMatchNote[] =
        props.effectiveReconciliationMatchNotes ??
        (invoice?.reconciliationIssues as { reconciliationMatchNotes?: ReconciliationMatchNote[] } | undefined)
            ?.reconciliationMatchNotes ??
        [];

    const rejectedInReturnsParts =
        (invoice?.reconciliationIssues as {
            rejectedInReturnsParts?: Array<{ partIndex: number; partNumber: string; tooltip?: string }>;
        } | undefined)?.rejectedInReturnsParts ?? [];

    const rejectedNotes = rejectedInReturnsParts.map((rej) => {
        const currentIdxForRej = useDirectIndex
            ? rej.partIndex
            : lineItemBackendIndexByCurrentIndex.findIndex((bi) => bi === rej.partIndex);
        const item = currentIdxForRej >= 0 ? lineItems[currentIdxForRej] : undefined;
        return {
            partIndex: rej.partIndex,
            invoicePartNumber: item?.partNumber ?? rej.partNumber,
            nexPartNumber: rej.partNumber,
            reason:
                rej.tooltip ??
                'Part was rejected/returned by the customer (found in NEX returns for this RO).',
        };
    });

    const matchNotes = [
        ...baseMatchNotes,
        ...rejectedNotes.filter(
            (rn) =>
                !baseMatchNotes.some(
                    (bn) =>
                        bn.partIndex === rn.partIndex &&
                        bn.invoicePartNumber === rn.invoicePartNumber &&
                        bn.nexPartNumber === rn.nexPartNumber &&
                        bn.reason === rn.reason
                )
        ),
    ];
    const matchNoteByPartIndex = new Map<number, ReconciliationMatchNote>();
    if (useDirectIndex) {
        matchNotes.forEach((n) => matchNoteByPartIndex.set(n.partIndex, n));
    } else {
        lineItemBackendIndexByCurrentIndex.forEach((backendIdx, currentIdx) => {
            if (backendIdx != null) {
                const note = matchNotes.find((n) => n.partIndex === backendIdx);
                if (note) matchNoteByPartIndex.set(currentIdx, note);
            }
        });
    }

    const manualMatchPartIndices = new Set((invoice?.manualMatches ?? []).map((m) => m.partIndex));
    const hasPersistedManualMatchForLineCurrentIndex = (currentIdx: number): boolean => {
        if (useDirectIndex) return manualMatchPartIndices.has(currentIdx);
        const bi = lineItemBackendIndexByCurrentIndex[currentIdx];
        return bi != null && manualMatchPartIndices.has(bi);
    };

    const matchedLineExpandKeys: string[] = [];
    lineItems.forEach((item, index) => {
        if (matchedNexByCurrentIndex[index]) matchedLineExpandKeys.push(getLineItemExpandKey(index, item));
    });
    const allMatchedNexRowsExpanded =
        matchedLineExpandKeys.length > 0 && matchedLineExpandKeys.every((k) => expandedMatchedNexRows.has(k));

    const gridEditableInputProps = invoicePartGridLandingInputPropsWithCaretAtEnd();

    return (
        <>
            <InvoicePartGridTableContainer
                containerRef={spreadsheet.containerRef}
                isReadOnly={!!isReadOnly}
                onMouseDownCapture={spreadsheet.onContainerMouseDownCapture}
                onPaste={spreadsheet.onPaste}
            >
                <Table variant="simple" size="sm" sx={getInvoiceItemsTableSx(showTableGridlines)}>
                    <Thead>
                        <InvoiceStandardHeaderRow
                            firstColumnContent={
                                <InvoiceMatchedNexExpandAllHeaderControl
                                    isVisible={matchedLineExpandKeys.length > 0}
                                    allExpanded={allMatchedNexRowsExpanded}
                                    onToggle={() => {
                                        setExpandedMatchedNexRows((prev) => {
                                            const next = new Set(prev);
                                            if (allMatchedNexRowsExpanded) matchedLineExpandKeys.forEach((k) => next.delete(k));
                                            else matchedLineExpandKeys.forEach((k) => next.add(k));
                                            return next;
                                        });
                                    }}
                                />
                            }
                            qtyHeaderContent={
                                <Tooltip label={qtyColumnTooltip} hasArrow placement="top">
                                    <Text as="span" display="inline-block" cursor="help" textTransform="uppercase" letterSpacing="0.04em" whiteSpace="nowrap">
                                        {qtyColumnLabel}
                                    </Text>
                                </Tooltip>
                            }
                            listPriceHeaderContent={
                                buildListPriceHeaderContent({
                                    invoiceType: invoice?.invoiceType,
                                    subletListPriceMarkup: invoice?.subletListPriceMarkup,
                                    subletListPriceDiscount: invoice?.subletListPriceDiscount,
                                    lkqListPriceMarkup: invoice?.lkqListPriceMarkup,
                                    lkqListPriceDiscount: invoice?.lkqListPriceDiscount,
                                }) ?? 'List Price'
                            }
                        />
                    </Thead>
                    <Tbody>
                        {lineItems.map((item, index) => {
                            const isUserUnmatched = unmatchedPartIndices.has(index);
                            const isFlagged = !suppressFlagAndSuggestionUi && (flaggedPartIndices.has(index) || isUserUnmatched);
                            const isManuallyAdded = (item.confidence ?? 0) === 0;
                            const issues = getIssuesForPart(index);
                            const matchedNex = matchedNexByCurrentIndex[index] as { partDesc?: string; partNumber?: string; qtyOnOrder?: number; qty?: number; listPrice?: number; unitCost?: number; extendedCost?: number; poHeaderID?: number; lineNumber?: number; unqSeq?: number | null; _poVendorName?: string } | undefined;
                            const hasMatchedNex = !!matchedNex;
                            const matchNote = matchNoteByPartIndex.get(index);
                            const hasMatchNote = !!matchNote;
                            const userConfirmedNexLineMatch =
                                Object.prototype.hasOwnProperty.call(localMatchedNexOverrides, index) ||
                                hasPersistedManualMatchForLineCurrentIndex(index);
                            const issuesForMatchedRowFlag = userConfirmedNexLineMatch
                                ? issues.filter((i: { issueType?: string }) => !isNexLineSelectionIssue(i))
                                : issues;
                            /** Orange tint: unmatched flags, matched rows with reconciliation issues (e.g. amount), or manual lines. */
                            const showRowHighlight =
                                isManuallyAdded ||
                                (isFlagged && (!hasMatchedNex || issuesForMatchedRowFlag.length > 0));
                            /** Unmatched only: matched rows use the blue chevron (no duplicate lightbulb). */
                            const showUnmatchedSuggestionBulb =
                                !suppressFlagAndSuggestionUi && !hasMatchedNex && !roNotFound && !isReadOnly;
                            /** When the primary unmatched bulb is suppressed (e.g. vendor mismatch), still offer NEX picking — only for unmatched lines. */
                            const showNexLineMatchPicker =
                                !isReadOnly &&
                                !roNotFound &&
                                !hasMatchedNex &&
                                !showUnmatchedSuggestionBulb &&
                                !vendorMismatchDisablesSuggestions;
                            const lineItemExpandKey = getLineItemExpandKey(index, item);
                            const isNexExpanded = expandedMatchedNexRows.has(lineItemExpandKey);
                            const nexQty = getSignedNexLineDisplayQty(
                                matchedNex,
                                invoice?.invoiceType,
                                nexLineQuantityBasis,
                            );
                            const nexTotalRaw = matchedNex?.extendedCost ?? (Math.abs(nexQty) * (matchedNex?.unitCost ?? 0));
                            const nexTotal = getSignedNexLineDisplayTotal(nexTotalRaw, invoice?.invoiceType);
                            const nexMatchedVendorName = matchedNex?._poVendorName?.trim() ?? '';
                            const { label: nexMatchedPoLineLabel, status: nexMatchedStatus } = formatNexMatchedLinePoLabel(
                                matchedNex,
                                invoice?.nexResponse ?? null,
                            );
                            const matchedNexGridlineColor = showTableGridlines ? 'gray.200' : 'transparent';
                            return (
                                <React.Fragment key={index}>
                                    <Tr
                                        bg={showRowHighlight ? 'orange.50' : 'white'}
                                        borderLeft={showRowHighlight ? '3px solid' : undefined}
                                        borderLeftColor={showRowHighlight ? 'orange.400' : undefined}
                                        _hover={{ bg: showRowHighlight ? 'orange.100' : 'gray.50' }}
                                        role="group"
                                        {...rowDrag.getDraggableRowProps(index)}
                                        onMouseEnter={() => {
                                            if (item.imageSourceHighlight) {
                                                onLineItemImageHighlightChange?.(item.imageSourceHighlight);
                                            }
                                        }}
                                        onMouseLeave={() => {
                                            onLineItemImageHighlightChange?.(null);
                                        }}
                                    >
                                        <Td py={3} px={2} borderColor="gray.100" position="relative" w="116px" minW="116px">
                                            <InvoicePartTableRowActionsCell
                                                isReadOnly={!!isReadOnly}
                                                onAddAbove={() => addLineItemAbove(index)}
                                                onAddBelow={() => addLineItemAt(index)}
                                                onDuplicate={() => duplicateLineItemAt(index)}
                                                onDelete={() => removeLineItem(index)}
                                            />
                                            <HStack {...invoicePartTableActionsIconStackProps}>
                                                    {hasMatchedNex && (
                                                        <InvoiceMatchedNexToggleButton
                                                            isExpanded={isNexExpanded}
                                                            onClick={() =>
                                                                setExpandedMatchedNexRows((prev) => {
                                                                    const next = new Set(prev);
                                                                    if (next.has(lineItemExpandKey)) next.delete(lineItemExpandKey);
                                                                    else next.add(lineItemExpandKey);
                                                                    return next;
                                                                })
                                                            }
                                                        />
                                                    )}
                                                    {!suppressFlagAndSuggestionUi && hasMatchedNex && isFlagged && issuesForMatchedRowFlag.length > 0 && (
                                                        <InvoiceIssueFlagButton tooltip={getIssueTooltip(issuesForMatchedRowFlag)} />
                                                    )}
                                                    {!suppressFlagAndSuggestionUi && !hasMatchedNex && isUserUnmatched && (
                                                        <InvoiceIssueFlagButton tooltip="Line item will be added to NEX without a matching NEX line" ariaLabel="Orphaned line item" />
                                                    )}
                                                    {!suppressFlagAndSuggestionUi && !hasMatchedNex && !isUserUnmatched && isFlagged && issues.length > 0 && (
                                                        <InvoiceIssueFlagButton tooltip={getIssueTooltip(issues)} />
                                                    )}
                                                    {!suppressFlagAndSuggestionUi && !hasMatchedNex && !roNotFound && !isReadOnly && (
                                                        <InvoiceSuggestionBulbButton
                                                            tooltip={getPartNumberChoiceIssue(index) ? 'Part number differs from NEX' : getMissingInNexSuggestion(index) ? 'Part not found – possible match' : 'Click to view suggested matching line items from NEX'}
                                                            onClick={() => {
                                                                const iss = getIssuesForPart(index);
                                                                openReconciliationDetailModal({ type: 'flag', partIndex: index, issue: iss[0] });
                                                            }}
                                                        />
                                                    )}
                                                    {showNexLineMatchPicker && (
                                                        <InvoiceSuggestionBulbButton
                                                            tooltip={
                                                                hasMatchedNex
                                                                    ? 'Review or change NEX line match'
                                                                    : 'Choose NEX line match'
                                                            }
                                                            onClick={() => {
                                                                const iss = getIssuesForPart(index);
                                                                openReconciliationDetailModal({
                                                                    type: 'flag',
                                                                    partIndex: index,
                                                                    ...(iss[0] ? { issue: iss[0] } : {}),
                                                                });
                                                            }}
                                                        />
                                                    )}
                                                    {vendorMismatchDisablesSuggestions && !hasMatchedNex && !roNotFound && !isReadOnly && (
                                                        <InvoiceSuggestionBulbButton
                                                            tooltip={VENDOR_MISMATCH_SUGGESTION_DISABLED_REASON}
                                                            disabled
                                                            ariaLabel="Suggestion disabled due to vendor mismatch"
                                                        />
                                                    )}
                                                    {hasMatchNote && matchNote && (
                                                        <InvoiceMatchNoteInfoButton
                                                            tooltip={matchNote.reason}
                                                            onClick={() =>
                                                                openReconciliationDetailModal({
                                                                    type: 'match_note',
                                                                    matchNote: {
                                                                        invoicePartNumber: matchNote.invoicePartNumber,
                                                                        nexPartNumber: matchNote.nexPartNumber,
                                                                        reason: matchNote.reason,
                                                                        partIndex: index,
                                                                        ...(matchNote.extractedFromPage
                                                                            ? { extractedFromPage: matchNote.extractedFromPage }
                                                                            : {}),
                                                                    },
                                                                })
                                                            }
                                                        />
                                                    )}
                                            </HStack>
                                        </Td>
                                        <Td
                                            py={3}
                                            fontSize="sm"
                                            textAlign="left"
                                            borderColor="gray.100"
                                            data-line-paste-cell
                                            data-row={index}
                                            data-col={0}
                                            sx={spreadsheet.getCellPasteSelectionSx(index, 0)}
                                        >
                                            <HStack spacing={1} align="center">
                                                <Box flex="1" minW={0} textAlign="left">
                                                    <LineItemGridLandingEditable
                                                        value={getLineItemStringValue(index, 'title', '')}
                                                        onChange={(v) => updateLineItem(index, 'title', v)}
                                                        placeholder="Description"
                                                        isReadOnly={!!isReadOnly}
                                                        editableRootProps={{ w: '100%' }}
                                                        editablePreviewProps={{
                                                            display: 'block',
                                                            w: '100%',
                                                            textAlign: 'left',
                                                            color: 'gray.900',
                                                            py: 0,
                                                            px: 0,
                                                        }}
                                                        editableInputProps={{ ...gridEditableInputProps, textAlign: 'left' }}
                                                    />
                                                </Box>
                                                <Box w="16px" minW="16px" flexShrink={0} display="flex" justifyContent="center">{!isReadOnly && showLineItemValueChangedRevert(index, 'title') && <Tooltip label="Value changed" placement="top" hasArrow><Center as="span" w="18px" h="18px" minW="18px" minH="18px" borderRadius="full" border="1.5px solid" borderColor="orange.400" display="inline-flex" flexShrink={0}><IconButton aria-label="Compare" icon={<Icon as={TbArrowBackUp as React.ElementType} w={3} h={3} color="orange.500" />} size="xs" variant="ghost" onClick={() => openRevertModal(`lineItem-${index}-title`, `Line ${index + 1} – Description`)} _hover={{ bg: 'orange.50' }} minW="auto" h="auto" p={0} flexShrink={0} tabIndex={-1} /></Center></Tooltip>}</Box>
                                            </HStack>
                                        </Td>
                                        <Td
                                            py={3}
                                            fontSize="sm"
                                            textAlign="left"
                                            borderColor="gray.100"
                                            whiteSpace="nowrap"
                                            minW="120px"
                                            data-line-paste-cell
                                            data-row={index}
                                            data-col={1}
                                            sx={spreadsheet.getCellPasteSelectionSx(index, 1)}
                                        >
                                            <HStack spacing={1} align="center" wrap="nowrap">
                                                <LineItemGridLandingEditable
                                                    value={getLineItemStringValue(index, 'partNumber', '')}
                                                    onChange={(v) => updateLineItem(index, 'partNumber', v)}
                                                    placeholder="Part #"
                                                    isReadOnly={!!isReadOnly}
                                                    editableRootProps={{ flex: '1', minW: 0 }}
                                                    editablePreviewProps={{
                                                        w: '100%',
                                                        cursor: 'pointer',
                                                        color: 'gray.900',
                                                        py: 0,
                                                        px: 0,
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                    editableInputProps={{ ...gridEditableInputProps, textAlign: 'left' }}
                                                />
                                                <Box w="16px" minW="16px" flexShrink={0} display="flex" justifyContent="center">{!isReadOnly && showLineItemValueChangedRevert(index, 'partNumber') && <Tooltip label="Value changed" placement="top" hasArrow><Center as="span" w="18px" h="18px" minW="18px" minH="18px" borderRadius="full" border="1.5px solid" borderColor="orange.400" display="inline-flex" flexShrink={0}><IconButton aria-label="Compare" icon={<Icon as={TbArrowBackUp as React.ElementType} w={3} h={3} color="orange.500" />} size="xs" variant="ghost" onClick={() => openRevertModal(`lineItem-${index}-partNumber`, `Line ${index + 1} – Part number`)} _hover={{ bg: 'orange.50' }} minW="auto" h="auto" p={0} flexShrink={0} tabIndex={-1} /></Center></Tooltip>}</Box>
                                            </HStack>
                                        </Td>
                                        <Td
                                            py={3}
                                            fontSize="sm"
                                            textAlign="right"
                                            borderColor="gray.100"
                                            data-line-paste-cell
                                            data-row={index}
                                            data-col={2}
                                            sx={spreadsheet.getCellPasteSelectionSx(index, 2)}
                                        >
                                            <Tooltip label={qtyColumnTooltip} hasArrow placement="top">
                                                <Box as="span" display="block">
                                                    <HStack spacing={0} align="center" justify="flex-end">
                                                        <LineItemGridLandingEditable
                                                            value={getLineItemStringValue(index, 'qty', '0')}
                                                            onChange={(v) => updateLineItem(index, 'qty', parseSignedQtyCommit(v))}
                                                            isReadOnly={!!isReadOnly}
                                                            editableRootProps={{ flex: '1', textAlign: 'right' }}
                                                            editablePreviewProps={{
                                                                w: '100%',
                                                                cursor: 'pointer',
                                                                textAlign: 'right',
                                                                color: 'gray.900',
                                                                py: 0,
                                                                px: 0,
                                                            }}
                                                            editableInputProps={{
                                                                type: 'text',
                                                                inputMode: 'text',
                                                                ...gridEditableInputProps,
                                                                textAlign: 'right',
                                                            }}
                                                        />
                                                        <Box w="16px" minW="16px" flexShrink={0} display="flex" justifyContent="center">{!isReadOnly && showLineItemValueChangedRevert(index, 'qty') && <Tooltip label="Value changed" placement="top" hasArrow><Center as="span" w="18px" h="18px" minW="18px" minH="18px" borderRadius="full" border="1.5px solid" borderColor="orange.400" display="inline-flex" flexShrink={0}><IconButton aria-label="Compare" icon={<Icon as={TbArrowBackUp as React.ElementType} w={3} h={3} color="orange.500" />} size="xs" variant="ghost" onClick={() => openRevertModal(`lineItem-${index}-qty`, `Line ${index + 1} – Quantity`)} _hover={{ bg: 'orange.50' }} minW="auto" h="auto" p={0} flexShrink={0} tabIndex={-1} /></Center></Tooltip>}</Box>
                                                    </HStack>
                                                </Box>
                                            </Tooltip>
                                        </Td>
                                        <Td
                                            py={3}
                                            fontSize="sm"
                                            textAlign="right"
                                            borderColor="gray.100"
                                            whiteSpace="nowrap"
                                            minW="90px"
                                            data-line-paste-cell
                                            data-row={index}
                                            data-col={3}
                                            sx={spreadsheet.getCellPasteSelectionSx(index, 3)}
                                        >
                                            <HStack spacing={1} align="center" justify="flex-end">
                                                <Box flex="1" minW={0} textAlign="right">
                                                    {isReadOnly ? fmtDollar(item.listPrice ?? 0) : renderLineItemNumericCell(index, 'listPrice')}
                                                </Box>
                                                {hasMatchNote &&
                                                    matchNote?.extractedFromPage &&
                                                    Math.abs(
                                                        (matchNote.extractedFromPage.listPrice ?? 0) -
                                                            (item.listPrice ?? 0)
                                                    ) > 0.005 && (
                                                        <InvoiceMatchNoteInfoButton
                                                            tooltip={matchNote.reason}
                                                            onClick={() =>
                                                                openReconciliationDetailModal({
                                                                    type: 'match_note',
                                                                    matchNote: {
                                                                        invoicePartNumber: matchNote.invoicePartNumber,
                                                                        nexPartNumber: matchNote.nexPartNumber,
                                                                        reason: matchNote.reason,
                                                                        partIndex: index,
                                                                        extractedFromPage: matchNote.extractedFromPage,
                                                                    },
                                                                })
                                                            }
                                                        />
                                                    )}
                                            </HStack>
                                        </Td>
                                        <Td
                                            py={3}
                                            fontSize="sm"
                                            textAlign="right"
                                            borderColor="gray.100"
                                            whiteSpace="nowrap"
                                            minW="90px"
                                            data-line-paste-cell
                                            data-row={index}
                                            data-col={4}
                                            sx={spreadsheet.getCellPasteSelectionSx(index, 4)}
                                        >
                                            <HStack spacing={1} align="center" justify="flex-end">
                                                <Box flex="1" minW={0} textAlign="right">
                                                    {isReadOnly
                                                        ? fmtDollar(item.unitBuyPrice ?? 0, 6)
                                                        : renderLineItemNumericCell(index, 'unitBuyPrice')}
                                                </Box>
                                                {hasMatchNote &&
                                                    matchNote?.extractedFromPage &&
                                                    Math.abs(
                                                        (matchNote.extractedFromPage.unitBuyPrice ?? 0) -
                                                            (item.unitBuyPrice ?? 0)
                                                    ) > 0.0005 && (
                                                        <InvoiceMatchNoteInfoButton
                                                            tooltip={matchNote.reason}
                                                            onClick={() =>
                                                                openReconciliationDetailModal({
                                                                    type: 'match_note',
                                                                    matchNote: {
                                                                        invoicePartNumber: matchNote.invoicePartNumber,
                                                                        nexPartNumber: matchNote.nexPartNumber,
                                                                        reason: matchNote.reason,
                                                                        partIndex: index,
                                                                        extractedFromPage: matchNote.extractedFromPage,
                                                                    },
                                                                })
                                                            }
                                                        />
                                                    )}
                                            </HStack>
                                        </Td>
                                        <Td
                                            py={3}
                                            fontSize="sm"
                                            textAlign="right"
                                            fontWeight="medium"
                                            borderColor="gray.100"
                                            whiteSpace="nowrap"
                                            minW="90px"
                                            data-line-paste-cell
                                            data-row={index}
                                            data-col={5}
                                            sx={spreadsheet.getCellPasteSelectionSx(index, 5)}
                                        >
                                            {isReadOnly ? fmtDollar(item.totalBuyPrice ?? 0) : renderLineItemNumericCell(index, 'totalBuyPrice', 'medium')}
                                        </Td>
                                    </Tr>
                                    {hasMatchedNex && isNexExpanded && (
                                        <Tr
                                            sx={{
                                                ...invoiceMatchedNexDetailDataRowTrSx,
                                                '& > td + td': {
                                                    borderLeft: '1px solid',
                                                    borderLeftColor: matchedNexGridlineColor,
                                                },
                                            }}
                                            onMouseEnter={() => {
                                                if (item.imageSourceHighlight) {
                                                    onLineItemImageHighlightChange?.(item.imageSourceHighlight);
                                                }
                                            }}
                                            onMouseLeave={() => {
                                                onLineItemImageHighlightChange?.(null);
                                            }}
                                        >
                                            <Td py={1.5} border="none" w="116px" minW="116px" px={2} bg="blue.50" />
                                            <Td py={1.5} fontSize="sm" textAlign="left" border="none" bg="blue.50">
                                                <HStack spacing={1} align="center" wrap="nowrap">
                                                    <Box flex="1" minW={0}>{(matchedNex?.partDesc ?? '').trim() || '—'}</Box>
                                                    <Box w="16px" minW="16px" flexShrink={0} />
                                                </HStack>
                                            </Td>
                                            <Td py={1.5} fontSize="sm" textAlign="left" border="none" bg="blue.50">
                                                <HStack spacing={1} align="center" wrap="nowrap">
                                                    <Box flex="1" minW={0}>{matchedNex?.partNumber ?? '—'}</Box>
                                                    <Box w="16px" minW="16px" flexShrink={0} />
                                                </HStack>
                                            </Td>
                                            <Td py={1.5} fontSize="sm" textAlign="right" border="none" bg="blue.50">
                                                <Tooltip label={qtyColumnTooltip} hasArrow placement="top">
                                                    <Box as="span" display="block" cursor="default">
                                                        <HStack spacing={0} align="center" justify="flex-end">
                                                            <Box w="100%" textAlign="right" px={0}>{nexQty}</Box>
                                                            <Box w="16px" minW="16px" flexShrink={0} />
                                                        </HStack>
                                                    </Box>
                                                </Tooltip>
                                            </Td>
                                            <Td py={1.5} fontSize="sm" textAlign="right" border="none" bg="blue.50">
                                                <HStack spacing={0} align="center" justify="flex-end" whiteSpace="nowrap">
                                                    <Box w="100%" textAlign="right">{matchedNex?.listPrice != null ? fmtDollar(matchedNex.listPrice) : '—'}</Box>
                                                </HStack>
                                            </Td>
                                            <Td py={1.5} fontSize="sm" textAlign="right" border="none" bg="blue.50">
                                                <HStack spacing={0} align="center" justify="flex-end" whiteSpace="nowrap">
                                                    <Box w="100%" textAlign="right">{matchedNex?.unitCost != null ? fmtDollar(matchedNex.unitCost, 6) : '—'}</Box>
                                                </HStack>
                                            </Td>
                                            <Td py={1.5} fontSize="sm" textAlign="right" fontWeight="medium" border="none" bg="blue.50">
                                                <HStack spacing={0} align="center" justify="flex-end" whiteSpace="nowrap">
                                                    <Box w="100%" textAlign="right">{fmtDollar(nexTotal)}</Box>
                                                </HStack>
                                            </Td>
                                        </Tr>
                                    )}
                                    {hasMatchedNex && (
                                        <Tr
                                            onMouseEnter={() => {
                                                if (item.imageSourceHighlight) {
                                                    onLineItemImageHighlightChange?.(item.imageSourceHighlight);
                                                }
                                            }}
                                            onMouseLeave={() => {
                                                onLineItemImageHighlightChange?.(null);
                                            }}
                                        >
                                            <Td colSpan={7} py={0} px={0} border="none" verticalAlign="top" sx={invoiceMatchedNexCollapseHeaderTdSx}>
                                                <Collapse in={isNexExpanded} animateOpacity>
                                                    <Flex w="100%" align="stretch" gap={0}>
                                                        <Flex {...invoiceMatchedNexActionsColumnFlexProps} py={2}>
                                                            <HStack {...invoicePartTableActionsIconStackProps}>
                                                                {!isReadOnly && (
                                                                    <Tooltip label="Unmatch NEX line" placement="top" hasArrow>
                                                                        <IconButton
                                                                            aria-label="Unmatch NEX line"
                                                                            icon={<Icon as={TbUnlink as React.ElementType} boxSize={4} />}
                                                                            size="xs"
                                                                            variant="ghost"
                                                                            color="gray.500"
                                                                            _hover={{ bg: 'red.50', color: 'red.600' }}
                                                                            minW="auto"
                                                                            h="auto"
                                                                            p={0}
                                                                            flexShrink={0}
                                                                            tabIndex={-1}
                                                                            onClick={() =>
                                                                                setUnmatchConfirmModal({
                                                                                    partIndex: index,
                                                                                    partNumber: item?.partNumber,
                                                                                    nexPartNumber: matchedNex?.partNumber,
                                                                                    nexLine: matchedNex,
                                                                                })
                                                                            }
                                                                        />
                                                                    </Tooltip>
                                                                )}
                                                            </HStack>
                                                        </Flex>
                                                            <Flex
                                                                flex="1"
                                                                minW={0}
                                                                bg="blue.50"
                                                                align="center"
                                                                justify="space-between"
                                                                gap={2}
                                                                flexWrap="wrap"
                                                                borderLeft="1px solid"
                                                                borderLeftColor={showTableGridlines ? 'gray.200' : 'transparent'}
                                                                py={2}
                                                                pl={4}
                                                                pr={4}
                                                            >
                                                                <HStack spacing={2} align="center" flex="1" minW={0} >
                                                                    <Text fontSize="xs" fontWeight="semibold" color="blue.700" as="span">
                                                                        Matched NEX Line Information
                                                                        {nexMatchedVendorName ? (
                                                                            <Text as="span" fontWeight="normal" color="blue.600" ml={2}>
                                                                                {nexMatchedVendorName}
                                                                            </Text>
                                                                        ) : null}
                                                                    </Text>
                                                                    <NexMatchSourceBadge source={(matchedNex as { _nexMatchSource?: 'returns' | 'orders' } | undefined)?._nexMatchSource} />
                                                                </HStack>
                                                                {(nexMatchedPoLineLabel || nexMatchedStatus) ? (
                                                                    <HStack spacing={2} flexShrink={0}>
                                                                        {nexMatchedPoLineLabel && (
                                                                            <Text fontSize="xs" fontWeight="normal" color="blue.600" whiteSpace="nowrap">
                                                                                {nexMatchedPoLineLabel}
                                                                            </Text>
                                                                        )}
                                                                        {nexMatchedStatus && (
                                                                            <Tag size="sm" colorScheme={getLineStatusTagColor(nexMatchedStatus)} variant="subtle" whiteSpace="nowrap">
                                                                                {getLineStatusDisplayText(nexMatchedStatus)}
                                                                            </Tag>
                                                                        )}
                                                                    </HStack>
                                                                ) : null}
                                                            </Flex>
                                                    </Flex>
                                                </Collapse>
                                            </Td>
                                        </Tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </Tbody>
                </Table>
            </InvoicePartGridTableContainer>
        </>
    );
}
